import { Alert, Chip, Group, Loader, NavLink, Select, Stack, Text, TextInput } from '@common/ui'
import { useEffect, useRef, useState } from 'react'
import { com } from '@eidu/web'
import useRegistryAssets from '../../../../io/useRegistryAssets'
import useRegistryAsset from '../../../../io/useRegistryAsset'
import PublishableEntityKinds = com.eidu.entity.publish.PublishableEntityKinds

const KIND_OPTIONS = ['', ...Array.from(PublishableEntityKinds.asJsReadonlySetView()).map((k) => k.toString())]

const formatDate = (ms: number) => new Date(ms).toLocaleString()

type RegistryAssetPickerProps = {
  onSelect: (entityId: string, versionId: string) => void
  selectedEntityId?: string
  selectedVersionId?: string
  kind?: string
}

type PickedAssetDetailProps = {
  entityId: string
  initialVersionId?: string
  onSelect: (entityId: string, versionId: string) => void
}

const PickedAssetDetail = ({ entityId, initialVersionId, onSelect }: PickedAssetDetailProps) => {
  const { detail, isLoading: detailLoading } = useRegistryAsset(entityId)
  const [pickedVersion, setPickedVersion] = useState<string | undefined>(initialVersionId)
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect

  useEffect(() => {
    if (!detail || pickedVersion !== undefined) return
    const latest = detail.versions[0]?.versionId
    if (latest) {
      setPickedVersion(latest)
      onSelectRef.current(detail.entityId, latest)
    }
  }, [detail, pickedVersion])

  const onPickVersion = (versionId: string) => {
    setPickedVersion(versionId)
    if (detail) onSelectRef.current(detail.entityId, versionId)
  }

  if (!detail) return null
  return (
    <>
      <Text fw={500}>{detail.name}</Text>
      <Text size="xs" c="dimmed">
        Entity {detail.entityId.slice(0, 8)}…
      </Text>
      {detailLoading ? (
        <Loader size="xs" />
      ) : (
        <Select
          label="Pin to version"
          size="sm"
          value={pickedVersion ?? ''}
          onChange={(next) => next && onPickVersion(next)}
          allowDeselect={false}
          data={detail.versions.map((v) => ({
            value: v.versionId,
            label: `${v.versionId.slice(0, 8)}… · ${formatDate(v.createdAt)}`,
          }))}
        />
      )}
    </>
  )
}

const RegistryAssetPicker = ({
  onSelect,
  selectedEntityId,
  selectedVersionId,
  kind = undefined,
}: RegistryAssetPickerProps) => {
  const [search, setSearch] = useState('')
  const [selectedKind, setSelectedKind] = useState('')
  const [pickedEntityId, setPickedEntityId] = useState<string | undefined>(selectedEntityId)
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const selectedRowRef = useRef<HTMLButtonElement | null>(null)
  const didScrollToSelectedRef = useRef(false)

  const effectiveKind = kind ?? (selectedKind || undefined)
  const { assets, loadMore, hasMore, loading, error } = useRegistryAssets({
    search: search.trim() || undefined,
    kind: effectiveKind,
  })

  useEffect(() => {
    const node = sentinelRef.current
    if (!node) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading) loadMore()
      },
      { rootMargin: '256px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, loading, loadMore])

  const selectedInList = !!selectedEntityId && assets.some((a) => a.entityId === selectedEntityId)
  useEffect(() => {
    if (didScrollToSelectedRef.current || !selectedInList) return
    selectedRowRef.current?.scrollIntoView({ block: 'center' })
    didScrollToSelectedRef.current = true
  }, [selectedInList])
  useEffect(() => {
    if (selectedEntityId && !selectedInList && hasMore && !loading) loadMore()
  }, [selectedEntityId, selectedInList, hasMore, loading, loadMore])

  return (
    <Group gap="md" align="stretch" wrap="nowrap" style={{ height: '100%' }}>
      <Stack gap="xs" flex={1} style={{ minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <TextInput
            label="Search"
            size="sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          {kind === undefined && (
            <Select
              label="Kind"
              size="sm"
              value={selectedKind}
              onChange={(next) => setSelectedKind(next ?? '')}
              allowDeselect={false}
              data={KIND_OPTIONS.map((k) => ({ value: k, label: k || 'Any' }))}
              style={{ width: 140 }}
            />
          )}
        </Group>
        {error && <Alert severity="negative">{error.message}</Alert>}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            border: '1px solid var(--trellis-color-outline)',
            borderRadius: 'var(--trellis-radius-xs)',
          }}
        >
          {assets.map((a) => (
            <NavLink
              component="button"
              key={a.entityId}
              ref={selectedEntityId === a.entityId ? selectedRowRef : undefined}
              active={selectedEntityId === a.entityId}
              onClick={() => setPickedEntityId(a.entityId)}
              label={
                <Group gap="xs">
                  <Text size="sm">{a.name}</Text>
                  <Chip>{a.kind}</Chip>
                </Group>
              }
              description={`Org ${a.publishingOrgId.slice(0, 8)}…`}
            />
          ))}
          <div ref={sentinelRef} />
          {loading && (
            <Group p="xs">
              <Loader size="xs" />
            </Group>
          )}
          {!loading && assets.length === 0 && (
            <Text size="sm" c="dimmed" p="xs">
              No published assets.
            </Text>
          )}
        </div>
      </Stack>
      <Stack gap="xs" style={{ width: 280 }}>
        {pickedEntityId ? (
          <PickedAssetDetail
            key={pickedEntityId}
            entityId={pickedEntityId}
            initialVersionId={pickedEntityId === selectedEntityId ? selectedVersionId : undefined}
            onSelect={onSelect}
          />
        ) : (
          <Text size="sm" c="dimmed">
            Pick an asset to choose a version.
          </Text>
        )}
      </Stack>
    </Group>
  )
}

export default RegistryAssetPicker
